import { useState, useEffect, createContext, useContext, useMemo, ReactNode, useCallback } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TPatientListColumnName, TPatientListTab } from 'src/types/patient-list'
import { useSocket } from 'src/providers/socket.provider'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { PatientService } from 'src/services/patient.service'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { TItemsPerPage } from 'src/types/others'
import { TPatient } from 'src/types/patient'
import {
  updatePatientMedTeamCommentsInCurrentPatientList,
  removePatientFromCurrentPatientList,
  updatePatientInCurrentPatientList,
} from './patients.helpers'
import {
  TPatientListRequest,
  TPatientListContext,
  TCurrentOrder,
  TPatientListResponse,
} from './patients.types'
import { EList, setListPreferedOrder, TOrderBy } from 'src/utils/list-order-preference.util'

const PATIENT_LIST_QUERY_KEY = 'patient-list'

const PatientListContext = createContext<TPatientListContext>({
  currentOrder: { column: undefined, descOrder: true },
  setRequestProps: () => {},
  updatePatientList: () => {},
  isPatientListSpoiled: true,
  currentTab: undefined,
  isLoading: false,
  error: undefined,
  data: undefined,
})

export const usePatientList = () => useContext(PatientListContext)

export const PatientListProvider = ({
  children,
  defaultOrderBy,
}: {
  children: ReactNode
  defaultOrderBy: TOrderBy<TPatientListColumnName>
}) => {
  //Hooks
  const { project, isFeatureActivated } = useCurrentProject()
  const { socket, socketError } = useSocket()

  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  //States
  const [requestProps, setRequestProps] = useState<TPatientListRequest | undefined>(
    project.PatientListTabs[0]
      ? {
          tabId: project.PatientListTabs[0]?.id,
          body: {
            pageNumber: 1,
            patientsPerPage: 50 as TItemsPerPage,
            orderBy: {
              column: 'PatientId' as TPatientListColumnName,
              descOrder: true,
            },
          },
        }
      : undefined,
  )

  const [currentTab, setCurrentTab] = useState<TPatientListTab | undefined>(
    project.PatientListTabs.find((tab) => tab.id === Number(searchParams.get('tabId'))) ??
      project.PatientListTabs[0],
  )

  const [currentOrder, setCurrentOrder] = useState<TCurrentOrder>(defaultOrderBy)

  const [isPatientListSpoiled, setIsPatientListSpoiled] = useState<boolean>(false)

  const updateQueryParams = (requestProps: TPatientListRequest) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('tabId', String(requestProps.tabId))
    params.set('column', String(requestProps.body.orderBy?.column))
    params.set('descOrder', String(requestProps.body.orderBy?.descOrder))
    params.set('page', String(requestProps.body.pageNumber))
    navigate(`?${params.toString()}`, { replace: true })
  }

  //Query
  const {
    isLoading,
    error,
    data: patientListResponse,
  } = useQuery({
    queryKey: [PATIENT_LIST_QUERY_KEY, requestProps],
    queryFn: () => {
      if (requestProps) {
        setIsPatientListSpoiled(false)
        updateQueryParams(requestProps)
        setCurrentOrder(requestProps.body.orderBy)
        setListPreferedOrder(EList.PATIENT_LIST, requestProps.body.orderBy)
        setCurrentTab(project.PatientListTabs.find((tab) => tab.id === requestProps.tabId))
        return requestProps && PatientService.getPatientListByTab(project.id, requestProps)
      }
    },
    enabled: !!requestProps,
  })

  //Handlers
  const updateQueryData = useCallback(
    (updatedPatientList: TPatient[]) => {
      queryClient.setQueryData(
        [PATIENT_LIST_QUERY_KEY, requestProps],
        (oldData: Partial<TPatientListResponse> | undefined) => {
          return { ...oldData, patients: updatedPatientList }
        },
      )
    },
    [queryClient, requestProps],
  )

  const handleUpdatePatient = useCallback(
    (data: { patient: TPatient }) => {
      if (!patientListResponse) return
      updatePatientInCurrentPatientList({
        displayRefreshButton: (isPatientListSpoiled) => setIsPatientListSpoiled(isPatientListSpoiled),
        updatePatientList: (updatedPatientList: TPatient[]) => updateQueryData(updatedPatientList),
        currentPatientList: patientListResponse.patients,
        updatedPatient: data.patient,
        currentOrder,
      })
    },
    [updateQueryData, setIsPatientListSpoiled, currentOrder, patientListResponse],
  )

  const handleDeletePatient = useCallback(
    (data: { patient: TPatient }) => {
      if (!patientListResponse) return
      removePatientFromCurrentPatientList({
        currentPatientList: patientListResponse.patients,
        removedPatient: data.patient,
        updatePatientList: (updatedPatientList: TPatient[]) => updateQueryData(updatedPatientList),
      })
    },
    [updateQueryData, patientListResponse],
  )

  const handleUpdateMedTeamComments = useCallback(
    (data: { patientId: number; medicalTeamCommentListCount: number }) => {
      if (!patientListResponse) return
      updatePatientMedTeamCommentsInCurrentPatientList({
        updatePatientList: (updatedPatientList: TPatient[]) => updateQueryData(updatedPatientList),
        medicalTeamCommentListCount: data.medicalTeamCommentListCount,
        currentPatientList: patientListResponse.patients,
        patientId: data.patientId,
      })
    },
    [updateQueryData, patientListResponse],
  )

  //First load
  const stringifiedProject = JSON.stringify(project)
  const stringifiedSearchParams = JSON.stringify(searchParams)

  useEffect(() => {
    if (!project?.PatientListTabs[0]) return
    const previousPatientListRequest = {
      tabId: Number(searchParams.get('tabId')),
      body: {
        pageNumber: Number(searchParams.get('page')),
        patientsPerPage: 50 as TItemsPerPage,
        orderBy: {
          column: searchParams.get('column') as TPatientListColumnName,
          descOrder: searchParams.get('descOrder') === 'true',
        },
      },
    }
    if (previousPatientListRequest.tabId) {
      setRequestProps(previousPatientListRequest)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedProject, stringifiedSearchParams])

  //WebSocket
  useEffect(() => {
    if (!socket || socketError) {
      return
    }
    socket.on('MEDICAL_TEAM_COMMENT', handleUpdateMedTeamComments)
    socket.on('PATIENT_DELETED', handleDeletePatient)
    socket.on('PATIENT_UPDATED', handleUpdatePatient)
    return () => {
      socket.off('MEDICAL_TEAM_COMMENT', handleUpdateMedTeamComments)
      socket.off('PATIENT_DELETED', handleDeletePatient)
      socket.off('PATIENT_UPDATED', handleUpdatePatient)
    }
  }, [
    handleUpdateMedTeamComments,
    handleUpdatePatient,
    handleDeletePatient,
    isFeatureActivated,
    socketError,
    project.id,
    socket,
  ])

  //Context values
  const providerValue = useMemo(
    (): TPatientListContext => ({
      data: patientListResponse,
      isPatientListSpoiled,
      setRequestProps,
      currentOrder,
      currentTab,
      isLoading,
      error: isLoading && !requestProps ? 'no patient list tabs' : error,
      updatePatientList: (args) => {
        if (args.method === 'PUT') {
          handleUpdatePatient(args.data)
        }
        if (args.method === 'DELETE') {
          handleDeletePatient(args.data)
        }
      },
    }),
    [
      isPatientListSpoiled,
      patientListResponse,
      handleUpdatePatient,
      handleDeletePatient,
      setRequestProps,
      requestProps,
      currentOrder,
      currentTab,
      isLoading,
      error,
    ],
  )

  return <PatientListContext.Provider value={providerValue}>{children}</PatientListContext.Provider>
}
